import React, { FC, useState, useEffect, useCallback, useRef } from "react";
import { Field } from "formik";
import {
  GoogleMap,
  Polyline,
  Polygon,
  Marker,
  InfoWindow,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { Libraries } from "@react-google-maps/api";
const MAP_LIBRARIES: Libraries = ["places"];
import {
  MapWrapper,
  Label,
  Placeholder,
  ErrorText,
  MapContainer,
  LoadingOverlay,
  InstructionsPanel,
  ControlPanel,
  StatsPanel,
  PolygonBadge,
  CurrentPathInfo,
  Button,
  ToggleButton,
  SearchContainer,
  SearchInput,
  SearchResults,
  SearchResultItem,
  SearchIcon,
  ClearButton,
} from "./style";

// Helper to generate consistent pleasant colors from string
const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Use HSL for better control. Restrict saturation and lightness for "slightly different" look
  // varied hues (0-360), consistent saturation (60-80%), consistent lightness (40-60%)
  const hue = Math.abs(hash % 360);
  const sat = 60 + (Math.abs(hash) % 20); // 60-80%
  const light = 45 + (Math.abs(hash) % 15); // 45-60%
  return `hsl(${hue}, ${sat}%, ${light}%)`;
};
import {
  MapPin,
  Navigation,
  Trash2,
  RotateCcw,
  Save,
  Edit3,
  X,
  Info,
  MousePointer,
  Square,
  ChevronDown,
  ChevronUp,
  Settings,
  CheckCircle,
  RefreshCcw,
  Search,
  ExternalLink,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";

interface MapProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  height?: string;
  onSelectPoint?: (
    latLng: { lat: number; lng: number },
    mapsUrl?: string,
  ) => void;
  selectedPoint?: { lat: number; lng: number; mapsUrl?: string } | null;
  backgroundPolygons?: { lat: number; lng: number }[][];
  allowDrawing?: boolean;
  otherStores?: Array<{
    id: number;
    name: string;
    module?: string;
    email?: string; // Added email
    phone?: string; // Added phone
    avatar?: string; // Added avatar
    polygons: { lat: number; lng: number }[][];
  }>;
}

const Map: FC<MapProps> = ({
  name,
  label,
  placeholder,
  disabled,
  height = "500px",
  onSelectPoint,
  selectedPoint: externalSelectedPoint,
  backgroundPolygons = [],
  allowDrawing = false,
  otherStores = [],
}) => {
  const navigate = useNavigate();
  const containerStyle = { width: "100%", height };

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const [polygons, setPolygons] = useState<
          { lat: number; lng: number }[][]
        >(Array.isArray(field.value) ? field.value : []);
        const [currentPath, setCurrentPath] = useState<
          { lat: number; lng: number }[]
        >([]);
        const [hoverPoint, setHoverPoint] = useState<{
          lat: number;
          lng: number;
        } | null>(null);
        const [hoveredStore, setHoveredStore] = useState<{
          id: number;
          name: string;
          module?: string;
          email?: string; // Added email
          phone?: string; // Added phone
          avatar?: string; // Added avatar
          position: { lat: number; lng: number };
        } | null>(null);

        // SELECTED POINT STATE
        const [internalSelectedPoint, setInternalSelectedPoint] = useState<{
          lat: number;
          lng: number;
          mapsUrl?: string;
        } | null>(
          field.value && field.value.lat && field.value.lng
            ? field.value
            : null,
        );

        const selectedPoint =
          externalSelectedPoint !== undefined
            ? externalSelectedPoint
            : internalSelectedPoint;

        // SEARCH STATE
        const [searchQuery, setSearchQuery] = useState("");
        const [searchResults, setSearchResults] = useState<
          google.maps.places.PlaceResult[]
        >([]);
        const [isSearching, setIsSearching] = useState(false);
        const [searchService, setSearchService] =
          useState<google.maps.places.PlacesService | null>(null);
        const [autocompleteService, setAutocompleteService] =
          useState<google.maps.places.AutocompleteSuggestion | null>(null);

        // Calculate initial center
        const calculateInitialCenter = useCallback(() => {
          if (externalSelectedPoint) {
            return {
              lat: externalSelectedPoint.lat,
              lng: externalSelectedPoint.lng,
            };
          }

          if (Array.isArray(field.value) && field.value.length > 0) {
            const allPoints = field.value.flat();
            if (allPoints.length > 0) {
              const avgLat =
                allPoints.reduce((sum, point) => sum + point.lat, 0) /
                allPoints.length;
              const avgLng =
                allPoints.reduce((sum, point) => sum + point.lng, 0) /
                allPoints.length;
              return { lat: avgLat, lng: avgLng };
            }
          }

          if (field.value && field.value.lat && field.value.lng) {
            return { lat: field.value.lat, lng: field.value.lng };
          }

          return { lat: 27.7172, lng: 85.324 };
        }, [field.value, externalSelectedPoint]);

        const [center, setCenter] = useState<{ lat: number; lng: number }>(
          calculateInitialCenter(),
        );
        const [loadingLocation, setLoadingLocation] = useState(true);
        const [mapMode, setMapMode] = useState<"view" | "draw" | any>("view");
        const [showControls, setShowControls] = useState(false);
        const [lastUpdate, setLastUpdate] = useState(Date.now());
        const [initialZoom, setInitialZoom] = useState(12);

        const polygonRefs = useRef<google.maps.Polygon[]>([]);
        const previousPaths = useRef<string[]>([]);
        const isUserInteracting = useRef(false);
        const mapRef = useRef<google.maps.Map | null>(null);
        const searchInputRef = useRef<HTMLInputElement>(null);
        const hasCenteredOnPolygons = useRef(false);

        // Refs for state access in event listeners
        const polygonsStateRef = useRef(polygons);
        const backgroundPolygonsRef = useRef(backgroundPolygons);

        // Keep refs synced
        useEffect(() => {
          polygonsStateRef.current = polygons;
        }, [polygons]);

        useEffect(() => {
          backgroundPolygonsRef.current = backgroundPolygons;
        }, [backgroundPolygons]);

        const { isLoaded } = useJsApiLoader({
          googleMapsApiKey: process.env.APP_MAP_API_KEY || "",
          libraries: MAP_LIBRARIES,
        });

        // Sync polygons when field.value changes
        useEffect(() => {
          if (Array.isArray(field.value)) {
            setPolygons(field.value);
          }
        }, [field.value]);

        // Generate Google Maps URL
        const generateMapsUrl = useCallback(
          (lat: number, lng: number): string => {
            return `https://www.google.com/maps?q=${lat},${lng}&z=15`;
          },
          [],
        );

        // Check if point is inside any polygon
        const isPointInPolygon = useCallback(
          (
            point: { lat: number; lng: number },
            polygon: { lat: number; lng: number }[],
          ): boolean => {
            if (!window.google || polygon.length < 3) return false;

            const googlePoint = new google.maps.LatLng(point.lat, point.lng);
            const googlePolygon = new google.maps.Polygon({
              paths: polygon.map((p) => new google.maps.LatLng(p.lat, p.lng)),
            });

            return google.maps.geometry.poly.containsLocation(
              googlePoint,
              googlePolygon,
            );
          },
          [],
        );

        // Check if point is inside any of the zones
        const isPointInAnyZone = useCallback(
          (point: { lat: number; lng: number }): boolean => {
            if (polygons.length === 0) return false;
            return polygons.some((polygon) => isPointInPolygon(point, polygon));
          },
          [polygons, isPointInPolygon],
        );

        // Helper to check if point is inside ANY background polygon
        const isPointInBackgroundPolygon = useCallback(
          (point: { lat: number; lng: number }): boolean => {
            if (backgroundPolygons.length === 0) return true; // No restrictions if no zones
            return backgroundPolygons.some((polygon) =>
              isPointInPolygon(point, polygon),
            );
          },
          [backgroundPolygons, isPointInPolygon],
        );

        // Initialize map and services
        const onMapLoad = useCallback(
          (map: google.maps.Map) => {
            mapRef.current = map;
            if (window.google) {
              setSearchService(new google.maps.places.PlacesService(map));
              setAutocompleteService(
                new google.maps.places.AutocompleteSuggestion(),
              );
            }
            if (!hasCenteredOnPolygons.current && polygons.length > 0) {
              centerMapOnPolygons();
              hasCenteredOnPolygons.current = true;
            }
          },
          [polygons],
        );

        // Center map on user location
        useEffect(() => {
          if (polygons.length === 0 && !selectedPoint) {
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  setCenter({
                    lat: pos.coords.latitude,
                    lng: pos.coords.longitude,
                  });
                  setLoadingLocation(false);
                },
                () => setLoadingLocation(false),
              );
            } else {
              setLoadingLocation(false);
            }
          } else {
            setLoadingLocation(false);
          }
        }, [polygons.length, selectedPoint]);

        // Center map on polygons
        const centerMapOnPolygons = useCallback(() => {
          if (!mapRef.current || polygons.length === 0) return;
          const bounds = new google.maps.LatLngBounds();
          polygons.forEach((polygon) => {
            polygon.forEach((point) => {
              bounds.extend(new google.maps.LatLng(point.lat, point.lng));
            });
          });
          mapRef.current.fitBounds(bounds);
          mapRef.current.panToBounds(bounds, 50);
        }, [polygons]);

        // Center map on selected point
        const centerMapOnSelectedPoint = useCallback(() => {
          if (!mapRef.current || !selectedPoint) return;
          mapRef.current.panTo({ lat: selectedPoint.lat, lng: selectedPoint.lng });
          mapRef.current.setZoom(16);
        }, [selectedPoint?.lat, selectedPoint?.lng]);

        // Auto-center when polygons change
        useEffect(() => {
          if (
            mapRef.current &&
            polygons.length > 0 &&
            !hasCenteredOnPolygons.current
          ) {
            centerMapOnPolygons();
            hasCenteredOnPolygons.current = true;
          }
        }, [polygons, centerMapOnPolygons]);

        // Auto-center when selected point changes
        useEffect(() => {
          if (mapRef.current && selectedPoint) {
            centerMapOnSelectedPoint();
          }
        }, [selectedPoint?.lat, selectedPoint?.lng, centerMapOnSelectedPoint]);

        // Get current polygon paths
        const getCurrentPathsAsString = useCallback(() => {
          return polygonRefs.current
            .filter((poly) => poly && typeof poly.getPath === "function" && poly.getPath())
            .map((poly) =>
              poly
                .getPath()
                .getArray()
                .map(
                  (latLng) =>
                    `${latLng.lat().toFixed(6)},${latLng.lng().toFixed(6)}`,
                )
                .join("|"),
            );
        }, []);

        // Force sync polygons
        const syncPolygons = useCallback(() => {
          const updated = polygonRefs.current
            .filter((poly) => poly && typeof poly.getPath === "function" && poly.getPath())
            .map((poly) =>
              poly
                .getPath()
                .getArray()
                .map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() })),
            );
          const currentString = getCurrentPathsAsString().join(";;");
          const previousString = previousPaths.current.join(";;");
          if (currentString !== previousString) {
            setPolygons(updated);
            form.setFieldValue(name, updated);
            previousPaths.current = getCurrentPathsAsString();
            setLastUpdate(Date.now());
          }
        }, [form, name, getCurrentPathsAsString]);

        // Polling for polygon sync
        useEffect(() => {
          if (onSelectPoint || polygons.length === 0) return;
          const interval = setInterval(() => {
            if (polygonRefs.current.length > 0) syncPolygons();
          }, 300);
          return () => clearInterval(interval);
        }, [onSelectPoint, polygons.length, syncPolygons]);

        // Polygon load
        const onPolygonLoad = useCallback(
          (polygon: google.maps.Polygon, index: number) => {
            if (!polygon) return;
            polygonRefs.current[index] = polygon;
            const path = polygon.getPath();
            if (!path) return;

            // Track changes for validation
            const validateAndRevert = () => {
              const currentBackgroundPolygons = backgroundPolygonsRef.current;
              // Helper to check if point is inside ANY background polygon using current ref
              const checkPointInBg = (point: { lat: number; lng: number }) => {
                if (currentBackgroundPolygons.length === 0) return true;
                return currentBackgroundPolygons.some((bgPoly) =>
                  isPointInPolygon(point, bgPoly),
                );
              };

              const pathObj = polygon.getPath();
              if (!pathObj) return;

              if (currentBackgroundPolygons.length > 0) {
                const newPath = pathObj.getArray();
                const isAllPointsInside = newPath.every((p) => {
                  const latLng = { lat: p.lat(), lng: p.lng() };
                  return checkPointInBg(latLng);
                });

                if (!isAllPointsInside) {
                  toast.warn(
                    "Boundary restriction: Polygon must stay within the zone.",
                  );
                  // Revert path
                  const oldPolygons = polygonsStateRef.current;
                  if (oldPolygons[index]) {
                    const oldPath = oldPolygons[index].map(
                      (p) => new google.maps.LatLng(p.lat, p.lng),
                    );
                    polygon.setPath(oldPath);
                  }
                  return;
                }
              }

              // If valid, update state
              // We use the Ref to get the base array to avoid stale closures, but we update the specific index
              // actually we should construct the new state from the current Ref, but with the modified path
              const currentPolygonsState = polygonsStateRef.current;
              const updated = [...currentPolygonsState];

              // Get the actual new path from the map object
              const newPathData = pathObj
                .getArray()
                .map((p) => ({
                  lat: p.lat(),
                  lng: p.lng(),
                }));

              updated[index] = newPathData;

              setPolygons(updated);
              form.setFieldValue(name, updated);
            };

            // Listeners for editing
            const listeners = [
              path.addListener("set_at", validateAndRevert),
              path.addListener("insert_at", validateAndRevert),
              path.addListener("remove_at", validateAndRevert),
              polygon.addListener("dragend", validateAndRevert),
            ];

            // Initial path string for simple change detection (optional)
            if (google.maps.geometry) {
              previousPaths.current[index] =
                google.maps.geometry.encoding.encodePath(path);
            }

            const mouseDownListener = google.maps.event.addListener(
              polygon,
              "mousedown",
              () => {
                isUserInteracting.current = true;
              },
            );
            const mouseUpListener = google.maps.event.addListener(
              window,
              "mouseup",
              () => {
                if (isUserInteracting.current) {
                  setTimeout(() => {
                    syncPolygons();
                    isUserInteracting.current = false;
                  }, 100);
                }
              },
            );
            return () => {
              listeners.forEach((listener) =>
                google.maps.event.removeListener(listener),
              );
              google.maps.event.removeListener(mouseDownListener);
              google.maps.event.removeListener(mouseUpListener);
            };
          },
          [
            isPointInPolygon,
            form,
            name,
            syncPolygons,
          ],
        );

        // Search functionality
        const handleSearch = useCallback(async (e?: any) => {
          if (e) {
            e.preventDefault();
            e.stopPropagation();
          }
          if (!searchQuery.trim() || !searchService || !mapRef.current) return;
          setIsSearching(true);
          setSearchResults([]);
          const request = {
            query: searchQuery,
            fields: ["name", "geometry", "formatted_address", "url"],
          };
          searchService.textSearch(request, (results, status) => {
            setIsSearching(false);
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              results
            ) {
              setSearchResults(results);
            } else {
              setSearchResults([]);
            }
          });
        }, [searchQuery, searchService]);

        // Search result selection - ONLY ALLOW IF INSIDE ZONE
        const handleResultSelect = useCallback(
          (place: google.maps.places.PlaceResult) => {
            if (!place.geometry || !place.geometry.location || !mapRef.current)
              return;
            const location = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };

            // Check if the searched location is inside any zone
            if (
              onSelectPoint &&
              polygons.length > 0 &&
              !isPointInAnyZone(location)
            ) {
              // Show some feedback that selection is not allowed
              alert(
                "This location is outside of the defined zones. Please select a location inside the marked areas.",
              );
              return;
            }

            const mapsUrl =
              place.url || generateMapsUrl(location.lat, location.lng);
            mapRef.current.panTo(location);
            mapRef.current.setZoom(16);
            setSearchQuery(place.formatted_address || place.name || "");
            setSearchResults([]);

            const pointWithUrl = { ...location, mapsUrl };

            if (onSelectPoint) {
              onSelectPoint(location, mapsUrl);
            }

            if (externalSelectedPoint === undefined) {
              setInternalSelectedPoint(pointWithUrl);
            }
            form.setFieldValue(name, pointWithUrl);
          },
          [
            onSelectPoint,
            form,
            name,
            generateMapsUrl,
            externalSelectedPoint,
            polygons,
            // isPointInAnyZone,
          ],
        );

        // Handle Enter key in search
        const handleSearchKeyPress = useCallback(
          (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              handleSearch();
            }
          },
          [handleSearch],
        );

        // Clear search
        const clearSearch = useCallback(() => {
          setSearchQuery("");
          setSearchResults([]);
        }, []);

        // Check if point is close to first point
        const isCloseToFirstPoint = useCallback(
          (point: { lat: number; lng: number }) => {
            if (currentPath.length < 3) return false;
            const firstPoint = currentPath[0];
            return (
              Math.abs(point.lat - firstPoint.lat) < 0.001 &&
              Math.abs(point.lng - firstPoint.lng) < 0.001
            );
          },
          [currentPath],
        );

        // Complete the current polygon
        const completePolygon = useCallback(() => {
          if (currentPath.length >= 3) {
            const closed = [...currentPath, currentPath[0]];
            const newPolygons = [...polygons, closed];
            setPolygons(newPolygons);
            form.setFieldValue(name, newPolygons);
            setCurrentPath([]);
            setHoverPoint(null);
            setMapMode("view");
            setTimeout(() => centerMapOnPolygons(), 100);
          }
        }, [currentPath, polygons, form, name, centerMapOnPolygons]);

        // Handle marker click for completion
        const onMarkerClick = useCallback(
          (index: number) => {
            if (mapMode !== "draw" || currentPath.length < 3) return;
            if (index === 0) completePolygon();
          },
          [mapMode, currentPath, completePolygon],
        );

        // Map click handler - RESTRICT TO ZONES ONLY
        const onMapClick = useCallback(
          (event: google.maps.MapMouseEvent) => {
            if (disabled || !event.latLng) return;
            const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
            const mapsUrl = generateMapsUrl(point.lat, point.lng);

            // 1. Polygon Drawing Mode (High Priority)
            if (mapMode === "draw") {
              // Restrict drawing to background polygons if they exist
              if (
                backgroundPolygons.length > 0 &&
                !isPointInBackgroundPolygon(point)
              ) {
                toast.warn("You can only draw inside the selected zone.");
                return;
              }
              if (currentPath.length >= 3 && isCloseToFirstPoint(point)) {
                completePolygon();
                return;
              }
              setCurrentPath([...currentPath, point]);
              return;
            }

            // 2. Point Selection Mode (Default if onSelectPoint exists)
            if (onSelectPoint) {
              // Check if point is inside any background polygon
              if (
                backgroundPolygons.length > 0 &&
                !isPointInBackgroundPolygon(point)
              ) {
                toast.warn(
                  "Please select a location inside the marked zones (gray areas).",
                );
                return;
              }

              // Legacy check for 'polygons' if no backgroundPolygons (optional, kept for safety)
              if (
                backgroundPolygons.length === 0 &&
                polygons.length > 0 &&
                !isPointInAnyZone(point)
              ) {
                toast.warn(
                  "Please select a location inside the marked zones (blue areas).",
                );
                return;
              }

              const pointWithUrl = { ...point, mapsUrl };

              onSelectPoint(point, mapsUrl);

              if (externalSelectedPoint === undefined) {
                setInternalSelectedPoint(pointWithUrl);
              }
              // If allowDrawing is true, we assume 'name' field is for Polygons, so DO NOT overwrite it with point
              if (!allowDrawing) {
                form.setFieldValue(name, pointWithUrl);
              }
              return;
            }

            // Polygon drawing logic
            if (mapMode !== "draw") return;

            // Restrict drawing to background polygons if they exist
            if (
              backgroundPolygons.length > 0 &&
              !isPointInBackgroundPolygon(point)
            ) {
              toast.warn("You can only draw inside the selected zone.");
              return;
            }

            if (currentPath.length >= 3 && isCloseToFirstPoint(point)) {
              completePolygon();
              return;
            }
            setCurrentPath([...currentPath, point]);
          },
          [
            currentPath,
            disabled,
            form,
            name,
            onSelectPoint,
            mapMode,
            isCloseToFirstPoint,
            completePolygon,
            generateMapsUrl,
            externalSelectedPoint,
            polygons,
            isPointInAnyZone,
            backgroundPolygons,
            isPointInBackgroundPolygon,
          ],
        );

        // Map mouse move
        const onMapMouseMove = useCallback(
          (event: google.maps.MapMouseEvent) => {
            if (!event.latLng || currentPath.length === 0) return;
            if ((!onSelectPoint || allowDrawing) && mapMode === "draw") {
              setHoverPoint({
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
              });
            }
          },
          [currentPath, onSelectPoint, mapMode],
        );

        // Remove polygon
        const removePolygon = useCallback(
          (index: number) => {
            if (disabled) return;
            const updated = polygons.filter((_, i) => i !== index);
            setPolygons(updated);
            form.setFieldValue(name, updated);
            polygonRefs.current = polygonRefs.current.filter(
              (_, i) => i !== index,
            );
            previousPaths.current = previousPaths.current.filter(
              (_, i) => i !== index,
            );
          },
          [polygons, disabled, form, name],
        );

        // Center on user location
        const centerOnUser = useCallback(() => {
          if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((pos) => {
              const location = {
                lat: pos.coords.latitude,
                lng: pos.coords.longitude,
              };
              setCenter(location);
              if (mapRef.current) {
                mapRef.current.panTo(location);
                mapRef.current.setZoom(16);
              }
            });
          }
        }, []);

        // Center on existing polygons
        const centerOnPolygons = useCallback(
          () => centerMapOnPolygons(),
          [centerMapOnPolygons],
        );

        // Start drawing new polygon
        const startDrawing = useCallback(() => {
          setCurrentPath([]);
          setHoverPoint(null);
          setMapMode("draw");
        }, []);

        // Cancel current drawing
        const cancelDrawing = useCallback(() => {
          setCurrentPath([]);
          setHoverPoint(null);
          setMapMode("view");
        }, []);

        // Clear all polygons
        const clearAllPolygons = useCallback(() => {
          setPolygons([]);
          setCurrentPath([]);
          setHoverPoint(null);
          form.setFieldValue(name, []);
          polygonRefs.current = [];
          previousPaths.current = [];
          setMapMode("view");
          hasCenteredOnPolygons.current = false;
        }, [form, name]);

        // Clear selected point
        const clearSelectedPoint = useCallback(() => {
          if (externalSelectedPoint === undefined) {
            setInternalSelectedPoint(null);
          }
          form.setFieldValue(name, null);
          if (onSelectPoint) {
            onSelectPoint(null as any, undefined);
          }
        }, [form, name, externalSelectedPoint, onSelectPoint]);

        // Manual force sync
        const forceSync = useCallback(() => syncPolygons(), [syncPolygons]);

        // Refresh everything
        const refreshMap = useCallback(() => {
          syncPolygons();
          setLastUpdate(Date.now());
        }, [syncPolygons]);

        // Open Google Maps in new tab
        const openInGoogleMaps = useCallback(() => {
          if (selectedPoint && selectedPoint.mapsUrl) {
            window.open(selectedPoint.mapsUrl, "_blank");
          }
        }, [selectedPoint]);

        if (!isLoaded) {
          return (
            <MapWrapper>
              {label && <Label>{label}</Label>}
              <LoadingOverlay>
                <div className="spinner" />
                <p>Loading Google Maps...</p>
              </LoadingOverlay>
            </MapWrapper>
          );
        }

        const canCompletePolygon = currentPath.length >= 3;
        const isNearFirstPoint =
          hoverPoint &&
          currentPath.length >= 3 &&
          isCloseToFirstPoint(hoverPoint);

        return (
          <MapWrapper>
            {label && <Label>{label}</Label>}

            <InstructionsPanel>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "4px",
                }}
              >
                <Info size={14} />
                <strong>
                  {onSelectPoint
                    ? "Select Location"
                    : mapMode === "draw"
                      ? "Drawing"
                      : "View"}
                </strong>
              </div>
              <p style={{ margin: 0, fontSize: "0.75rem", color: "#9ca3af" }}>
                {onSelectPoint
                  ? "Click on the map to select a location"
                  : mapMode === "draw"
                    ? "Click to add points, complete by clicking first marker"
                    : "Edits auto-save every 300ms"}
              </p>
              {selectedPoint && onSelectPoint && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "6px 8px",
                    backgroundColor: "#ecfdf5",
                    border: "1px solid #d1fae5",
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                  }}
                >
                  <strong style={{ fontSize: "0.75rem" }}>Selected:</strong>
                  <br />
                  {selectedPoint.lat.toFixed(6)}, {selectedPoint.lng.toFixed(6)}
                  {selectedPoint.mapsUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="small"
                      onClick={openInGoogleMaps}
                      style={{
                        marginTop: "4px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        fontSize: "0.6875rem",
                        padding: "0.25rem 0.5rem",
                      }}
                    >
                      <ExternalLink size={10} /> Open
                    </Button>
                  )}
                </div>
              )}
            </InstructionsPanel>

            <MapContainer>
              <SearchContainer>
                <SearchIcon 
                  onClick={handleSearch} 
                  style={{ cursor: "pointer", pointerEvents: "auto" }}
                >
                  <Search size={16} />
                </SearchIcon>
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search for locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />
                {searchQuery && (
                  <ClearButton onClick={clearSearch}>
                    <X size={14} />
                  </ClearButton>
                )}
                {searchResults.length > 0 && (
                  <SearchResults>
                    {searchResults.map((result, index) => (
                      <SearchResultItem
                        key={index}
                        onClick={() => handleResultSelect(result)}
                      >
                        <div style={{ fontWeight: 500, fontSize: "14px" }}>
                          {result.name}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b" }}>
                          {result.formatted_address}
                        </div>
                      </SearchResultItem>
                    ))}
                  </SearchResults>
                )}
              </SearchContainer>

              <ToggleButton
                type="button"
                onClick={() => setShowControls(!showControls)}
                isOpen={showControls}
              >
                <Settings size={16} />
                {showControls ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </ToggleButton>

              {showControls && (
                <ControlPanel>
                  <StatsPanel>
                    {(!onSelectPoint || allowDrawing) && (
                      <>
                        <PolygonBadge>
                          {polygons.length} Polygon
                          {polygons.length !== 1 ? "s" : ""}
                        </PolygonBadge>
                        <Button
                          type="button"
                          variant="danger"
                          size="small"
                          onClick={() => {
                            clearAllPolygons();
                            if (onSelectPoint) {
                              // Maybe keeping point? Or clearing it too?
                              // Usually clear polygons clears polygons.
                            }
                          }}
                          disabled={disabled}
                        >
                          <Trash2 size={12} /> Clear Polygons
                        </Button>
                      </>
                    )}
                    {currentPath.length > 0 && (
                      <PolygonBadge style={{ background: "#ff8c00" }}>
                        {currentPath.length} Points
                      </PolygonBadge>
                    )}
                    {isNearFirstPoint && (
                      <PolygonBadge style={{ background: "#10b981" }}>
                        Ready to Complete
                      </PolygonBadge>
                    )}
                    <PolygonBadge
                      style={{ background: "#3b82f6", fontSize: "10px" }}
                    >
                      Last: {new Date(lastUpdate).toLocaleTimeString()}
                    </PolygonBadge>
                    {onSelectPoint && selectedPoint && (
                      <PolygonBadge style={{ background: "#10b981" }}>
                        Point Selected
                      </PolygonBadge>
                    )}
                  </StatsPanel>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {(!onSelectPoint || allowDrawing) && (
                      <>
                        {mapMode === "view" ? (
                          <Button
                            type="button"
                            onClick={startDrawing}
                            disabled={disabled}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Square size={16} /> Start Drawing
                          </Button>
                        ) : (
                          <CurrentPathInfo>
                            <span>
                              {currentPath.length} point
                              {currentPath.length !== 1 ? "s" : ""} placed
                              {isNearFirstPoint &&
                                " • Click first marker to complete"}
                            </span>
                            <div style={{ display: "flex", gap: "4px" }}>
                              {canCompletePolygon && (
                                <Button
                                  type="button"
                                  variant="primary"
                                  size="small"
                                  onClick={completePolygon}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    flex: 1,
                                  }}
                                >
                                  <CheckCircle size={14} /> Complete
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="small"
                                onClick={cancelDrawing}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  flex: 1,
                                }}
                              >
                                <X size={14} /> Cancel
                              </Button>
                            </div>
                          </CurrentPathInfo>
                        )}
                        {polygons.length > 0 && (
                          <>
                            <Button
                              type="button"
                              variant="primary"
                              onClick={centerOnPolygons}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Navigation size={16} /> Center on Polygons
                            </Button>

                            <Button
                              type="button"
                              variant="danger"
                              onClick={clearAllPolygons}
                              disabled={disabled}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Trash2 size={16} /> Clear All
                            </Button>
                          </>
                        )}
                      </>
                    )}
                    {onSelectPoint && selectedPoint && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={openInGoogleMaps}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <ExternalLink size={16} /> Open in Maps
                        </Button>
                        {externalSelectedPoint === undefined && (
                          <Button
                            type="button"
                            variant="danger"
                            onClick={clearSelectedPoint}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Trash2 size={16} /> Clear Point
                          </Button>
                        )}
                      </>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={centerOnUser}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Navigation size={16} /> My Location
                    </Button>
                  </div>
                </ControlPanel>
              )}

              {loadingLocation && (
                <LoadingOverlay>
                  <div className="spinner small" />
                  <span>Getting your location...</span>
                </LoadingOverlay>
              )}

              <GoogleMap
                mapContainerStyle={containerStyle}
                center={center}
                zoom={initialZoom}
                onClick={onMapClick}
                onMouseMove={onMapMouseMove}
                onLoad={onMapLoad}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  zoomControl: true,
                  styles: [
                    {
                      featureType: "poi",
                      elementType: "labels",
                      stylers: [{ visibility: "on" }],
                    },
                  ],
                }}
              >
                {/* Other Stores Polygons */}
                {otherStores.map((store) => (
                  <React.Fragment key={`store-${store.id}`}>
                    {store.polygons.map((path, idx) => (
                      <Polygon
                        key={`store-${store.id}-poly-${idx}`}
                        paths={path}
                        options={{
                          fillColor: stringToColor(store.name),
                          fillOpacity: 0.35,
                          strokeColor: stringToColor(store.name),
                          strokeWeight: 2,
                          clickable: true,
                          zIndex: 10,
                          editable: false,
                          draggable: false,
                        }}
                        onMouseOver={(e) => {
                          if (e.latLng) {
                            setHoveredStore({
                              id: store.id,
                              name: store.name,
                              module: store.module,
                              email: store.email,
                              phone: store.phone,
                              avatar: store.avatar,
                              position: {
                                lat: e.latLng.lat(),
                                lng: e.latLng.lng(),
                              },
                            });
                          }
                        }}
                        onMouseOut={() => setHoveredStore(null)}
                      />
                    ))}
                  </React.Fragment>
                ))}

                {/* Hover InfoWindow */}
                {hoveredStore && (
                  <InfoWindow
                    position={hoveredStore.position}
                    onCloseClick={() => setHoveredStore(null)}
                    options={{
                      disableAutoPan: true,
                      pixelOffset: new window.google.maps.Size(0, -10),
                    }}
                  >
                    <div
                      style={{
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        minWidth: "200px",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      {/* Avatar or Initial Placeholder */}
                      <div
                        style={{
                          width: "48px",
                          height: "48px",
                          borderRadius: "50%",
                          overflow: "hidden",
                          flexShrink: 0,
                          backgroundColor: "#f3f4f6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid #fff",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        {hoveredStore.avatar ? (
                          <img
                            src={`${process.env.APP_BUCKET_URL}/${hoveredStore.avatar}`}
                            alt={hoveredStore.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              // Fallback if image fails
                              (e.target as HTMLImageElement).style.display =
                                "none";
                              (
                                e.target as HTMLImageElement
                              ).parentElement!.innerText = hoveredStore.name
                                .charAt(0)
                                .toUpperCase();
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              fontSize: "20px",
                              fontWeight: "bold",
                              color: "#6b7280",
                            }}
                          >
                            {hoveredStore.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "#111827",
                            marginBottom: "2px",
                          }}
                        >
                          {hoveredStore.name}
                        </div>
                        {hoveredStore.module && (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              fontWeight: 500,
                              marginBottom: "4px",
                            }}
                          >
                            {hoveredStore.module}
                          </span>
                        )}
                        {hoveredStore.email && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#4b5563",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>📧</span> {hoveredStore.email}
                          </div>
                        )}
                        {hoveredStore.phone && (
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#4b5563",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <span>📞</span> {hoveredStore.phone}
                          </div>
                        )}
                      </div>
                    </div>
                  </InfoWindow>
                )}

                {/* Background (Reference) Polygons */}
                {backgroundPolygons.map((path, idx) => (
                  <Polygon
                    key={`bg-${idx}`}
                    paths={path}
                    options={{
                      fillColor: "#9ca3af", // Gray for reference
                      fillOpacity: 0.15,
                      strokeColor: "#6b7280",
                      strokeWeight: 1,
                      strokeOpacity: 0.5,
                      clickable: false, // Make background transparent to clicks so map can handle drawing
                      zIndex: 1, // Lower Z-index
                      editable: false,
                      draggable: false,
                    }}
                  />
                ))}

                {/* ALWAYS show polygons */}
                {polygons.map((path, idx) => (
                  <Polygon
                    key={idx}
                    paths={path}
                    options={{
                      fillColor: onSelectPoint ? "#3b82f6" : "#ff8c00",
                      fillOpacity: onSelectPoint ? 0.2 : 0.3,
                      strokeColor: onSelectPoint ? "#1d4ed8" : "#ff8c00",
                      strokeWeight: onSelectPoint ? 3 : 2,
                      strokeOpacity: 0.8,
                      zIndex: 200, // Higher Z-index for active polygons
                      editable:
                        !disabled &&
                        mapMode === "view" &&
                        (!onSelectPoint || allowDrawing),
                      draggable:
                        !disabled &&
                        mapMode === "view" &&
                        (!onSelectPoint || allowDrawing),
                      clickable: !onSelectPoint || allowDrawing,
                    }}
                    onLoad={(poly) => onPolygonLoad(poly, idx)}
                    onRightClick={() =>
                      (!onSelectPoint || allowDrawing) && removePolygon(idx)
                    }
                  />
                ))}

                {selectedPoint && (
                  <Marker
                    position={selectedPoint}
                    icon={{
                      url: `data:image/svg+xml;base64,${btoa(`
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M16 2C11.58 2 8 5.58 8 10C8 15.55 16 30 16 30C16 30 24 15.55 24 10C24 5.58 20.42 2 16 2Z" fill="#3b82f6"/>
                          <path d="M16 13C17.6569 13 19 11.6569 19 10C19 8.34315 17.6569 7 16 7C14.3431 7 13 8.34315 13 10C13 11.6569 14.3431 13 16 13Z" fill="white"/>
                        </svg>
                      `)}`,
                      scaledSize: new window.google.maps.Size(32, 32),
                      anchor: new window.google.maps.Point(16, 32),
                    }}
                  />
                )}

                {(!onSelectPoint || allowDrawing) && currentPath.length > 0 && (
                  <>
                    <Polyline
                      path={currentPath}
                      options={{
                        strokeColor: "#ff8c00",
                        strokeWeight: 3,
                        strokeOpacity: 0.8,
                        clickable: false,
                      }}
                    />
                    {hoverPoint && (
                      <Polyline
                        path={[currentPath[currentPath.length - 1], hoverPoint]}
                        options={{
                          strokeColor: isNearFirstPoint ? "#10b981" : "#ff8c00",
                          strokeWeight: isNearFirstPoint ? 4 : 2,
                          strokeOpacity: 0.8,
                          clickable: false,
                        }}
                      />
                    )}
                    {currentPath.map((point, index) => (
                      <Marker
                        key={index}
                        position={point}
                        onClick={() => onMarkerClick(index)}
                        icon={{
                          url: `data:image/svg+xml;base64,${btoa(`
                            <svg width="${index === 0 && isNearFirstPoint ? "20" : "16"}" height="${index === 0 && isNearFirstPoint ? "20" : "16"}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <circle cx="8" cy="8" r="6" fill="${index === 0 && isNearFirstPoint ? "#10b981" : "#ff8c00"}" stroke="white" stroke-width="2"/>
                              ${index === 0 && isNearFirstPoint ? '<circle cx="8" cy="8" r="3" fill="white" stroke="white" stroke-width="1"/>' : ""}
                            </svg>
                          `)}`,
                          scaledSize: new window.google.maps.Size(
                            index === 0 && isNearFirstPoint ? 20 : 16,
                            index === 0 && isNearFirstPoint ? 20 : 16,
                          ),
                          anchor: new window.google.maps.Point(8, 8),
                        }}
                      />
                    ))}
                  </>
                )}
              </GoogleMap>
            </MapContainer>

            {placeholder && (!field.value || field.value.length === 0) && (
              <Placeholder>{placeholder}</Placeholder>
            )}
            {meta.touched && meta.error && <ErrorText>{meta.error}</ErrorText>}
          </MapWrapper>
        );
      }}
    </Field>
  );
};

export default Map;
